import { createFileRoute, Link } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Loader2, MessageSquarePlus, MessagesSquare } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { StrategyQAPanel } from "@/components/strategy/qa/StrategyQAPanel";
import { TopicChips } from "@/components/strategy/qa/TopicChips";
import { QuickPrompts } from "@/components/strategy/qa/QuickPrompts";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
	useStrategyConversations,
	useStrategyConversation,
	useStartConversation,
	useSendMessage,
} from "@/hooks/use-strategy-qa";
import { apiService } from "@/lib/api-service";
import type { QAConversationListItem } from "@/types/strategy-qa";

export const Route = createFileRoute("/_app/_/strategy/qa")({
	component: StrategyQAPage,
});

function StrategyQAPage() {
	const [activeConversationId, setActiveConversationId] = useState<
		number | null
	>(null);
	const [selectedTopic, setSelectedTopic] = useState<string | null>(null);

	const { data: conversationsData, isLoading: conversationsLoading } =
		useStrategyConversations();

	const { data: activeConversation } =
		useStrategyConversation(activeConversationId);

	const queryClient = useQueryClient();
	const startConversation = useStartConversation();

	const sendMessage = useSendMessage(activeConversationId ?? 0);

	// -----------------------------------------------------------------------
	// Handlers
	// -----------------------------------------------------------------------

	function handleStartConversation(topic?: string) {
		startConversation.mutate(topic, {
			onSuccess: (data) => {
				setActiveConversationId(data.id);
			},
			onError: (error: Error) => {
				toast.error(
					error.message || "Failed to start conversation.",
				);
			},
		});
	}

	function handleQuickPrompt(prompt: string) {
		// Start a new conversation and immediately send the prompt
		startConversation.mutate(selectedTopic ?? undefined, {
			onSuccess: async (data) => {
				setActiveConversationId(data.id);
				// Call API directly instead of through the hook, because
				// useSendMessage is bound to activeConversationId which
				// hasn't updated yet (React state is async).
				try {
					await apiService.sendStrategyQAMessage(data.id, prompt);
					queryClient.invalidateQueries({
						queryKey: ["strategy-qa", "conversation", data.id],
					});
				} catch (e) {
					console.error("Failed to send quick prompt:", e);
					toast.error("Failed to send quick prompt.");
				}
			},
			onError: (error: Error) => {
				toast.error(
					error.message || "Failed to start conversation.",
				);
			},
		});
	}

	function handleSendMessage(content: string) {
		if (!activeConversationId) return;
		sendMessage.mutate(content, {
			onError: (error: Error) => {
				toast.error(error.message || "Failed to send message.");
			},
		});
	}

	function handleSelectConversation(id: number) {
		setActiveConversationId(id);
	}

	// -----------------------------------------------------------------------
	// Render
	// -----------------------------------------------------------------------

	return (
		<div className="flex h-[calc(100vh-4rem)] flex-col">
			{/* Top bar */}
			<div className="border-b bg-background px-4 py-3">
				<div className="mx-auto flex max-w-6xl items-center justify-between">
					<div className="flex items-center gap-3">
						<Link
							to="/strategy"
							className="text-muted-foreground hover:text-foreground"
						>
							<ArrowLeft className="size-5" />
						</Link>
						<div>
							<h1 className="text-lg font-semibold">
								Strategy Q&A
							</h1>
							<p className="text-xs text-muted-foreground">
								Ask questions about your campaign strategy
							</p>
						</div>
					</div>
					<Button
						size="sm"
						variant="outline"
						onClick={() => {
							setActiveConversationId(null);
						}}
					>
						<MessageSquarePlus className="size-4 mr-1.5" />
						New Chat
					</Button>
				</div>
			</div>

			{/* Main content area */}
			<div className="flex flex-1 overflow-hidden">
				{/* Sidebar - conversation list */}
				<aside className="hidden w-72 shrink-0 border-r bg-muted/30 md:block">
					<div className="p-3">
						<h3 className="mb-2 text-xs font-semibold uppercase text-muted-foreground">
							Conversations
						</h3>
					</div>
					<ScrollArea className="h-[calc(100%-3rem)]">
						<div className="space-y-1 px-2">
							{conversationsLoading && (
								<div className="flex items-center justify-center py-8">
									<Loader2 className="size-5 animate-spin text-muted-foreground" />
								</div>
							)}
							{conversationsData?.conversations.map((conv) => (
								<ConversationListItem
									key={conv.id}
									conversation={conv}
									isActive={
										activeConversationId === conv.id
									}
									onClick={() =>
										handleSelectConversation(conv.id)
									}
								/>
							))}
							{!conversationsLoading &&
								conversationsData?.total === 0 && (
									<p className="px-2 py-6 text-center text-xs text-muted-foreground">
										No conversations yet.
										Start one below!
									</p>
								)}
						</div>
					</ScrollArea>
				</aside>

				{/* Chat area */}
				<main className="flex flex-1 flex-col overflow-hidden">
					{activeConversationId && activeConversation ? (
						<StrategyQAPanel
							messages={activeConversation.messages}
							onSendMessage={handleSendMessage}
							isSending={sendMessage.isPending}
							title={
								activeConversation.topic
									? `Topic: ${activeConversation.topic}`
									: undefined
							}
						/>
					) : (
						<EmptyState
							selectedTopic={selectedTopic}
							onSelectTopic={setSelectedTopic}
							onQuickPrompt={handleQuickPrompt}
							onStartConversation={handleStartConversation}
							isStarting={startConversation.isPending}
						/>
					)}
				</main>
			</div>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function ConversationListItem({
	conversation,
	isActive,
	onClick,
}: {
	conversation: QAConversationListItem;
	isActive: boolean;
	onClick: () => void;
}) {
	const date = new Date(conversation.updated_at);
	const timeStr = date.toLocaleDateString("en-US", {
		month: "short",
		day: "numeric",
	});

	return (
		<button
			type="button"
			onClick={onClick}
			className={`w-full rounded-lg px-3 py-2.5 text-left transition-colors ${
				isActive
					? "bg-primary/10 border border-primary/20"
					: "hover:bg-muted"
			}`}
		>
			<div className="flex items-center justify-between">
				<span className="text-xs font-medium truncate">
					{conversation.topic || "General"}
				</span>
				<span className="text-[10px] text-muted-foreground shrink-0 ml-2">
					{timeStr}
				</span>
			</div>
			{conversation.last_message_preview && (
				<p className="mt-0.5 text-xs text-muted-foreground line-clamp-2">
					{conversation.last_message_preview}
				</p>
			)}
			<span className="text-[10px] text-muted-foreground">
				{conversation.message_count} messages
			</span>
		</button>
	);
}

function EmptyState({
	selectedTopic,
	onSelectTopic,
	onQuickPrompt,
	onStartConversation,
	isStarting,
}: {
	selectedTopic: string | null;
	onSelectTopic: (topic: string | null) => void;
	onQuickPrompt: (prompt: string) => void;
	onStartConversation: (topic?: string) => void;
	isStarting: boolean;
}) {
	return (
		<div className="flex flex-1 flex-col items-center justify-center px-6 py-12">
			<div className="w-full max-w-lg space-y-8">
				{/* Icon and heading */}
				<div className="text-center">
					<div className="mx-auto mb-4 flex size-14 items-center justify-center rounded-full bg-primary/10">
						<MessagesSquare className="size-7 text-primary" />
					</div>
					<h2 className="text-xl font-semibold">
						Ask About Your Strategy
					</h2>
					<p className="mt-1.5 text-sm text-muted-foreground">
						Get answers grounded in your personalized
						campaign strategy.
					</p>
				</div>

				{/* Topic chips */}
				<div className="flex justify-center">
					<TopicChips
						selectedTopic={selectedTopic}
						onSelectTopic={onSelectTopic}
					/>
				</div>

				<Separator />

				{/* Quick prompts */}
				<div>
					<h3 className="mb-3 text-sm font-medium text-muted-foreground">
						Quick questions to get started
					</h3>
					<QuickPrompts onSelect={onQuickPrompt} />
				</div>

				<Separator />

				{/* Start blank conversation */}
				<div className="text-center">
					<Button
						onClick={() =>
							onStartConversation(selectedTopic ?? undefined)
						}
						disabled={isStarting}
					>
						{isStarting ? (
							<Loader2 className="size-4 animate-spin mr-1.5" />
						) : (
							<MessageSquarePlus className="size-4 mr-1.5" />
						)}
						Start a Conversation
					</Button>
				</div>
			</div>
		</div>
	);
}
